import React, { useState, useRef, useEffect } from "react";
import {
    formatDateHeader,
    cleanBotHtml,
    formatUserText,
    getInitials
} from "../utils/deepseekUtils";
import DeepSeekTurnDetailsModal from "./DeepSeekTurnDetailsModal";


const DeepSeekChatWindow = ({
    selectedUser,
    loadingHistory,
    groupedConversations,
    selectedConversationId,
    setSelectedConversationId,
    chatViewMode,
    setChatViewMode,
    t
}) => {
    const [selectedTurnForModal, setSelectedTurnForModal] = useState(null);

    useEffect(() => {
        setSelectedTurnForModal(null);
    }, [selectedConversationId]);

    const activeConv = groupedConversations.find(c => c.id === selectedConversationId) || groupedConversations[0];

    const messagesEndRef = useRef(null);

    const scrollToBottom = () => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
    };

    useEffect(() => {
        scrollToBottom();
    }, [activeConv?.id, activeConv?.turns?.length]);

    if (loadingHistory) {
        return (
            <div className="text-center py-5 w-100 h-100 d-flex flex-column align-items-center justify-content-center">
                <i className="fal fa-spinner fa-spin fa-2x text-primary mb-2"></i>
                <p className="small text-muted">{t('Processing') || "جاري جلب محادثات المستخدم..."}</p>
            </div>
        );
    }

    if (groupedConversations.length === 0) {
        return (
            <div className="text-center py-5 text-muted w-100 h-100 d-flex flex-column align-items-center justify-content-center">
                <i className="fal fa-comments-alt fa-3x mb-3 text-muted"></i>
                <h5>{t('Pages.DeepSeekAdmin.historyEmpty') || "لا توجد سجلات محادثة لهذا المستخدم."}</h5>
            </div>
        );
    }

    const initials = getInitials(selectedUser?.Name, t);

    return (
        <div className={`deepseek-conversation-split view-${chatViewMode}`}>
            {/* Right sub-sidebar: Conversations list */}
            <div className="deepseek-conv-sidebar">
                <div className="deepseek-conv-sidebar-header">
                    {t('Pages.DeepSeekAdmin.conversations') || "المحادثات المتاحة"}
                </div>
                <div className="deepseek-conv-list">
                    {groupedConversations.map(c => {
                        const isActive = activeConv.id === c.id;
                        const hasCacheHit = c.turns && c.turns.some(t => t.IsFromBot && t.Price === 0);
                        return (
                            <div
                                key={c.id}
                                className={`deepseek-conv-item ${isActive ? 'active' : ''}`}
                                onClick={() => {
                                    setSelectedConversationId(c.id);
                                    setChatViewMode("chat");
                                }}
                            >
                                <div className="deepseek-conv-title" title={c.title} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, textAlign: 'right' }}>
                                        {c.title}
                                    </span>
                                </div>
                                <div className="deepseek-conv-meta">
                                    <span className="date">{new Date(c.dateTime).toLocaleDateString('ar-EG')}</span>
                                    <div className="d-flex align-items-center gap-1">
                                        {hasCacheHit && (
                                            <span className="badge bg-soft-info text-info ml-1" style={{ fontSize: '9px', fontWeight: 'bold' }}>
                                                <i className="fal fa-bolt"></i> {t('Pages.DeepSeekAdmin.fromCache') || "من الكاش"}
                                            </span>
                                        )}
                                        <span className="price">${c.price.toFixed(4)}</span>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Left main area: Active conversation chat messages */}
            <div className="deepseek-conv-body">
                {activeConv ? (
                    <div className="deepseek-chat-stream w-100">
                        {/* Messages List styled like ChatPane.js */}
                        <div className="chat-messages-area p-0 bg-transparent border-0 shadow-none d-flex flex-column gap-3">
                            {activeConv.turns.map((turn, index) => {
                                const totalTimeMs = (turn.DBTime || 0) + (turn.AITime || 0);
                                const totalTimeSec = (totalTimeMs / 1000).toFixed(2);

                                const currentDate = new Date(turn.DateTime).toDateString();
                                const prevTurn = index > 0 ? activeConv.turns[index - 1] : null;
                                const prevDate = prevTurn ? new Date(prevTurn.DateTime).toDateString() : null;
                                const showDateHeader = currentDate !== prevDate;

                                return (
                                    <React.Fragment key={turn.Id}>
                                        {showDateHeader && (
                                            <div className="chat-date-separator text-center my-3 animate__animated animate__fadeIn">
                                                <span className="chat-date-badge px-3 py-1 bg-white text-muted border rounded-pill small font-weight-bold shadow-sm" style={{ fontSize: '11px', color: '#64748b' }}>
                                                    {formatDateHeader(turn.DateTime, t)}
                                                </span>
                                            </div>
                                        )}
                                        {/* User Prompt (Outgoing / Right-aligned in RTL) */}
                                        {!turn.IsFromBot && (
                                            <div className="message-row user animate__animated animate__fadeInUp animate__faster d-flex align-items-start justify-content-start gap-2">
                                                {/* User Avatar */}
                                                <div className="deepseek-avatar m-0 ml-2" style={{ width: '32px', height: '32px', fontSize: '12px' }}>
                                                    {selectedUser?.Photo ? (
                                                        <img
                                                            src={selectedUser.Photo.startsWith("http") ? selectedUser.Photo : `${selectedUser.Photo}`}
                                                            alt={selectedUser.Name}
                                                            style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }}
                                                        />
                                                    ) : (
                                                        initials
                                                    )}
                                                </div>
                                                <div className="message-bubble-wrapper">
                                                    <div className="message-bubble user">
                                                        <div className="user-message-text" dangerouslySetInnerHTML={{ __html: formatUserText(turn.Message || turn.Prompt) }} />
                                                        <div className="bubble-time-inside">
                                                            {new Date(turn.DateTime).toLocaleTimeString('ar-EG', { hour: '2-digit', minute: '2-digit' })}
                                                            {selectedUser?.SchoolName && selectedUser.UserTypeId !== 1 && selectedUser.UserTypeId !== 2 && selectedUser.UserTypeId !== 6 && (
                                                                <span className="mr-2">| {selectedUser.SchoolName}</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* Bot Answer (Incoming / Left-aligned in RTL) */}
                                        {turn.IsFromBot && (
                                            <div className="message-row bot animate__animated animate__fadeInUp animate__faster d-flex align-items-start justify-content-end gap-2">
                                                <div className="message-bubble-wrapper">
                                                    <div className="message-bubble bot">
                                                        {((turn.Message || turn.Answer || "").includes("CHAT_UNSPOURTED")) ? (
                                                            <div className="alert alert-warning border-0 m-0 py-2 px-3 d-flex align-items-center gap-2 text-right" style={{ borderRadius: '8px', color: '#856404', backgroundColor: '#fff3cd', direction: 'rtl' }}>
                                                                <i className="fal fa-exclamation-triangle ml-2" style={{ fontSize: '16px' }}></i>
                                                                <span>{t('Pages.DeepSeekAdmin.unsupportedMessageAlert') || "عذراً، هذا الاستفسار خارج نطاق اللغة العربية والتعليم المدرسي وغير مدعوم من النظام."}</span>
                                                            </div>
                                                        ) : ((turn.Message || turn.Answer || "").includes("[NEW_CHAT_REQUIRED]")) ? (
                                                            <div className="alert alert-warning border-0 m-0 py-2 px-3 d-flex align-items-center gap-2 text-right" style={{ borderRadius: '8px', color: '#856404', backgroundColor: '#fff3cd', direction: 'rtl' }}>
                                                                <i className="fal fa-exclamation-triangle ml-2" style={{ fontSize: '16px' }}></i>
                                                                <span>{t('Pages.DeepSeekAdmin.newChatRequiredAlert') || "هذه الرسالة غير متعلقة بالمواضيع الحالية."}</span>
                                                            </div>
                                                        ) : (
                                                            <div
                                                                className="bot-answer-html"
                                                                dangerouslySetInnerHTML={{ __html: cleanBotHtml(turn.Message || turn.Answer) }}
                                                            />
                                                        )}
                                                        <div className="bubble-time-inside">
                                                            {new Date(turn.DateTime).toLocaleTimeString('ar-EG', { hour: '2-digit', minute: '2-digit' })}
                                                        </div>
                                                    </div>

                                                    {/* Bot actions row: stats badge (always visible) */}
                                                    <div className="mt-1 d-flex justify-content-start">
                                                        <div
                                                            className="stats-badge total-time-badge d-flex align-items-center gap-1"
                                                            onClick={() => setSelectedTurnForModal(turn)}
                                                            style={{ cursor: 'pointer' }}
                                                            title={t('Pages.DeepSeekAdmin.actions') || "خيارات التحميل والتحكم"}
                                                        >
                                                            <i className="fal fa-robot"></i>
                                                            <span className="font-weight-bold">{turn.Agent}</span>
                                                            <span className="text-muted">|</span>
                                                            <i className="fal fa-clock"></i>
                                                            <span>{totalTimeSec}{t('Pages.Chatbot.SecondsAbbr') || "ث"}</span>
                                                            <span className="text-muted">|</span>
                                                            {turn.Price === 0 ? (
                                                                <span className="badge bg-soft-info text-info px-2 py-0.5 font-weight-bold" style={{ fontSize: '10px' }}>
                                                                    <i className="fal fa-bolt"></i> {t('Pages.DeepSeekAdmin.cached') || "من الكاش"}
                                                                </span>
                                                            ) : (
                                                                <span className="text-success font-weight-bold">${(turn.Price || 0).toFixed(5)}</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                                {/* Bot Avatar */}
                                                <div
                                                    className="deepseek-avatar m-0 mr-2 clickable-bot-avatar"
                                                    onClick={() => setSelectedTurnForModal(turn)}
                                                    style={{ width: '32px', height: '32px', fontSize: '12px', cursor: 'pointer' }}
                                                    title={t('Pages.DeepSeekAdmin.actions') || "خيارات التحميل والتحكم"}
                                                >
                                                    <i className="fal fa-robot"></i>
                                                </div>
                                            </div>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                            <div ref={messagesEndRef} />
                        </div>
                    </div>
                ) : (
                    <div className="text-center py-5 text-muted">
                        {t('Pages.DeepSeekAdmin.selectConversation') || "يرجى اختيار محادثة من القائمة لمشاهدة تفاصيلها."}
                    </div>
                )}
            </div>

            {/* AI Details Modal */}
            <DeepSeekTurnDetailsModal
                show={!!selectedTurnForModal}
                onHide={() => setSelectedTurnForModal(null)}
                turn={selectedTurnForModal}
                activeConvTurns={activeConv?.turns || []}
                t={t}
            />
        </div>
    );
};

export default DeepSeekChatWindow;
